import { useEffect, useMemo, useState } from "react";
import Layout from "@/components/feature/Layout";
import Breadcrumb from "@/components/base/Breadcrumb";
import NewsCard from "@/components/base/NewsCard";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { newsList, newsCategories } from "@/mocks/news";

const PAGE_SIZE = 20;

export default function NewsListPage() {
  const [category, setCategory] = useState("全部");
  const [page, setPage] = useState(1);

  useDocumentMeta({
    title: "日漫 - 动漫资讯中心，最新日本动画行业动态",
    description:
      "日漫资讯中心汇总最新日本动画行业新闻、作品报道、人物访谈与活动公告，按时间倒序更新，第一时间掌握动漫圈动态。",
    keywords: "动漫资讯,日本动画新闻,行业动态,作品报道,日漫",
  });

  useEffect(() => {
    setPage(1);
  }, [category]);

  const filtered = useMemo(() => {
    const list = category === "全部" ? newsList : newsList.filter((item) => item.category === category);
    return [...list].sort((a, b) => (a.date < b.date ? 1 : -1));
  }, [category]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const featured = filtered[0];

  return (
    <Layout>
      <div className="w-full px-4 md:px-6 pt-6">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "动漫资讯" }]} />
      </div>

      <section className="w-full px-4 md:px-6 pt-6 pb-4">
        <div className="rounded-lg bg-secondary-50 border border-secondary-100 p-6 md:p-8">
          <h1 className="font-heading text-2xl md:text-3xl font-extrabold text-foreground-950">动漫资讯中心</h1>
          <p className="mt-2 text-sm text-foreground-600 max-w-3xl">
            汇总日本动画行业的最新动态：定档速报、作品报道、制作访谈与活动公告，按发布时间倒序呈现。
          </p>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 pb-16">
        <div className="flex flex-wrap items-center gap-2 mb-6">
          {newsCategories.map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => setCategory(item)}
              className={`px-4 h-9 rounded-full text-sm border transition-colors cursor-pointer whitespace-nowrap ${
                category === item
                  ? "bg-primary-500 border-primary-500 text-background-50"
                  : "border-background-200 text-foreground-700 hover:border-primary-300 hover:text-primary-600"
              }`}
            >
              {item}
            </button>
          ))}
        </div>

        {pageItems.length > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
            <div className="lg:col-span-8">
              <div className="flex flex-col gap-4">
                {pageItems.map((news) => (
                  <NewsCard key={news.id} news={news} layout="horizontal" />
                ))}
              </div>
              <Pagination current={page} total={totalPages} onChange={setPage} />
            </div>

            <aside className="lg:col-span-4 space-y-6">
              {featured && (
                <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <h2 className="font-heading text-base font-bold text-foreground-950 mb-3 flex items-center gap-2">
                    <i className="ri-fire-line text-primary-500" />
                    头条推荐
                  </h2>
                  <NewsCard news={featured} />
                </div>
              )}
              <div className="rounded-lg border border-background-200 bg-secondary-50 p-5">
                <h2 className="font-heading text-base font-bold text-foreground-950 mb-3">资讯分类</h2>
                <ul className="space-y-2 text-sm">
                  {newsCategories
                    .filter((item) => item !== "全部")
                    .map((item) => {
                      const count = newsList.filter((news) => news.category === item).length;
                      return (
                        <li key={item}>
                          <button
                            type="button"
                            onClick={() => setCategory(item)}
                            className="w-full flex items-center justify-between px-3 h-9 rounded-md text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
                          >
                            <span>{item}</span>
                            <span className="text-xs text-foreground-500">{count} 篇</span>
                          </button>
                        </li>
                      );
                    })}
                </ul>
              </div>
            </aside>
          </div>
        ) : (
          <EmptyState
            title="该分类下暂无资讯"
            description="换个分类看看，或重置筛选浏览全部资讯内容。"
            actionLabel="查看全部资讯"
            onAction={() => setCategory("全部")}
          />
        )}
      </section>
    </Layout>
  );
}